import { useCallback, useEffect, useRef, useState } from "react"
import PhotoCard from "../../components/Photos/PhotoCard"
import { GalleryPhoto } from "../types"
import { scrollToGalleryTop } from "../../utils/scrollToGalleryTop"

const BATCH_SIZE = 90

// Default rhythm of tile heights to create a masonry feel. We don't have real
// image dimensions, so heights cycle deterministically by index.
const DEFAULT_HEIGHTS = [
  "h-[220px] md:h-[360px]",
  "h-[300px] md:h-[460px]",
  "h-[180px] md:h-[300px]",
  "h-[260px] md:h-[420px]",
  "h-[340px] md:h-[520px]",
  "h-[200px] md:h-[320px]",
]

interface MasonryCollectionProps {
  photos: GalleryPhoto[]
  /** Tailwind column-count classes, e.g. "columns-2 md:columns-3". */
  columnsClass?: string
  /** Column + row gap in pixels. */
  gap?: number
  /** Cycling tile-height classes. */
  heights?: string[]
}

/**
 * CSS-columns masonry with the same batched lazy-loading as the default
 * LayoutWrapper. Reuses PhotoCard so fullscreen, selection, watermark and
 * download-blocking behaviour are identical across designs.
 */
const MasonryCollection = ({
  photos,
  columnsClass = "columns-2 md:columns-3",
  gap = 10,
  heights = DEFAULT_HEIGHTS,
}: MasonryCollectionProps) => {
  const [loadedCount, setLoadedCount] = useState(BATCH_SIZE)
  const observerRef = useRef<HTMLDivElement | null>(null)
  const prevPhotosLength = useRef<number>(0)

  const loadMore = useCallback(() => {
    setLoadedCount((prev) => Math.min(prev + BATCH_SIZE, photos?.length || 0))
  }, [photos?.length])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore()
      },
      { threshold: 0.8 }
    )
    const target = observerRef.current
    if (target) observer.observe(target)
    return () => {
      if (target) observer.unobserve(target)
    }
  }, [loadMore])

  // On a folder/tab change (list grows after first load), jump to the folder
  // bar — not the page top / cover. Skip the first load and skip removals.
  useEffect(() => {
    const currentLength = photos?.length || 0
    const previousLength = prevPhotosLength.current
    if (previousLength !== 0 && currentLength >= previousLength) {
      scrollToGalleryTop()
    }
    prevPhotosLength.current = currentLength
    setLoadedCount(BATCH_SIZE)
  }, [photos])

  return (
    <div className="w-full">
      <div className={columnsClass} style={{ columnGap: `${gap}px` }}>
        {photos?.slice(0, loadedCount)?.map((item, i) => {
          // Prefer the photo's real aspect ratio (from backend dimensions) so
          // tiles are never cropped and there's no measure-then-reflow. Fall
          // back to the deterministic height rhythm for legacy/unmeasured files.
          const hasDims = !!item.width && !!item.height
          return (
            <div
              key={item.id}
              className={`break-inside-avoid ${hasDims ? "" : heights[i % heights.length]}`}
              style={{
                marginBottom: `${gap}px`,
                ...(hasDims ? { aspectRatio: `${item.width} / ${item.height}` } : {}),
              }}
            >
              <PhotoCard ratio="h-full" item={item} />
            </div>
          )
        })}
      </div>
      <div ref={observerRef} className="h-10 w-full" />
    </div>
  )
}

export default MasonryCollection
